import { useCallback } from 'react';
import { EUserRolesUI } from 'data/types/user.types';
import { ECapabilityKey, EPermissionType } from 'data/types/permissions.types';
import { EPermissionTypeRestriction, PERMISSION_TYPE_RESTRICTIONS } from 'data/datas/permissions.config';
import { useAppSelector } from 'redux/store';

/**
 * Hook to check the current user's permission capabilities and type-based restrictions.
 *
 * - can(key) — capability-based check (toggleable per user)
 * - canByType(restriction) — type-based restriction check (hardcoded per permission type)
 * - Impersonated admins are treated as Full Access
 */
export const usePmPermissions = () => {
  const usersData = useAppSelector((state) => state.usersSlice);
  const { users, userType, impersonateOn } = usersData;

  const isLandlord = userType === EUserRolesUI.LANDLORD;
  const isImpersonatingAdmin = !!impersonateOn;
  const profile =
    userType === EUserRolesUI.PROPERTY_MANAGER || userType === EUserRolesUI.LANDLORD ? users?.[userType] : null;
  const userPermissions = profile?.permissions;
  const accountHasReports = Boolean(profile?.hasAccessToReports);

  const permissionType =
    isLandlord || isImpersonatingAdmin ? EPermissionType.FULL : userPermissions?.type ?? EPermissionType.FULL;

  const capabilities = userPermissions && 'capabilities' in userPermissions ? userPermissions.capabilities : undefined;

  const isFullAccess = permissionType === EPermissionType.FULL;
  const isRead = permissionType === EPermissionType.READ;
  const isCustomIndividual = permissionType === EPermissionType.CUSTOM_INDIVIDUAL;
  const isCustomShared = permissionType === EPermissionType.CUSTOM_SHARED;

  /** Capability-based check (toggleable per user) */
  const can = useCallback(
    (key: ECapabilityKey | ECapabilityKey[]): boolean => {
      const checkSingle = (k: ECapabilityKey): boolean => {
        if (k === ECapabilityKey.AUTOMATED_REPORTING_ACCESS && !accountHasReports) return false;
        if (isFullAccess) return true;
        if (isRead) return false;
        if (!capabilities) return false;
        return capabilities[k];
      };

      if (Array.isArray(key)) {
        return key.some(checkSingle);
      }

      return checkSingle(key);
    },
    [isFullAccess, isRead, capabilities, accountHasReports]
  );

  /** Permission type-based restriction check (hardcoded, not toggleable) */
  const canByType = useCallback(
    (restriction: EPermissionTypeRestriction): boolean => {
      const allowedTypes = PERMISSION_TYPE_RESTRICTIONS[restriction];
      return allowedTypes.includes(permissionType);
    },
    [permissionType]
  );

  return {
    type: permissionType,
    permissions: userPermissions,
    can,
    canByType,
    isFullAccess,
    isRead,
    isCustomIndividual,
    isCustomShared,
    isImpersonatingAdmin,
    hasAccessToCourses: Boolean(profile?.hasAccessToCourses),
  };
};
